import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

function AiInsights({ practiceId }) {
  const { hasPermission } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [computing, setComputing] = useState(false);

  // Date range filter
  const [dateRange, setDateRange] = useState('30'); // '7', '30', '90', 'custom'
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const getDateParams = () => {
    if (dateRange === 'custom' && customFrom) {
      return { start_date: customFrom, end_date: customTo || new Date().toISOString().split('T')[0] };
    }
    if (dateRange === 'all') return {};
    const d = new Date();
    d.setDate(d.getDate() - parseInt(dateRange));
    return { start_date: d.toISOString().split('T')[0] };
  };

  // Detail data for specific tabs
  const [churnData, setChurnData] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [sentimentData, setSentimentData] = useState(null);
  const [ltvData, setLtvData] = useState(null);
  const [optimizations, setOptimizations] = useState(null);

  const loadDashboard = useCallback(async () => {
    if (!practiceId) return;
    try {
      setLoading(true);
      const dateParams = getDateParams();
      const [dashRes, configRes] = await Promise.all([
        api.getAiDashboard(practiceId, dateParams),
        api.getAiFeatureConfigs(practiceId),
      ]);
      if (dashRes.success) setDashboard(dashRes.data);
      if (configRes.success) setConfigs(configRes.data || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, dateRange, customFrom, customTo]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Clear cached tab data when date range changes so tabs reload with new params
  useEffect(() => {
    setChurnData(null); setForecastData(null); setSentimentData(null);
    setLtvData(null); setOptimizations(null);
  }, [dateRange, customFrom, customTo]);

  const loadTabData = useCallback(async (tab) => {
    if (!practiceId) return;
    const dateParams = getDateParams();
    try {
      if (tab === 'churn' && !churnData) {
        const [risk, dist] = await Promise.all([
          api.getChurnRisk(practiceId, { threshold: 0.4, limit: 20, ...dateParams }),
          api.getChurnDistribution(practiceId, dateParams),
        ]);
        setChurnData({ patients: risk.data || [], distribution: dist.data || [] });
      }
      if (tab === 'revenue' && !forecastData) {
        const res = await api.getRevenueForecast(practiceId, { days: 30, ...dateParams });
        setForecastData(res.data || []);
      }
      if (tab === 'sentiment' && !sentimentData) {
        const [frustrated, summary] = await Promise.all([
          api.getFrustratedPatients(practiceId, dateParams),
          api.getSentimentSummary(practiceId, dateParams),
        ]);
        setSentimentData({ frustrated: frustrated.data || [], summary: summary.data || {} });
      }
      if (tab === 'ltv' && !ltvData) {
        const [patients, dist] = await Promise.all([
          api.getPatientLtv(practiceId, { limit: 20, ...dateParams }),
          api.getLtvDistribution(practiceId, dateParams),
        ]);
        setLtvData({ patients: patients.data || [], distribution: dist.data || [] });
      }
      if (tab === 'campaigns' && !optimizations) {
        const res = await api.getCampaignOptimizations(practiceId, dateParams);
        setOptimizations(res.data || []);
      }
    } catch (err) {
      console.error(`Error loading ${tab} data:`, err.message);
      if (tab === 'sentiment') setSentimentData({ frustrated: [], summary: {} });
      if (tab === 'campaigns') setOptimizations([]);
      if (tab === 'churn') setChurnData({ patients: [], distribution: [] });
      if (tab === 'revenue') setForecastData([]);
      if (tab === 'ltv') setLtvData({ patients: [], distribution: [] });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId, churnData, forecastData, sentimentData, ltvData, optimizations, dateRange, customFrom, customTo]);

  useEffect(() => { loadTabData(activeTab); }, [activeTab, loadTabData]);

  const handleComputeNoShow = async () => {
    try {
      setComputing(true);
      await api.computeNoShowRisk(practiceId);
      await loadDashboard();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setComputing(false);
    }
  };

  const handleToggleFeature = async (featureType, enabled) => {
    try {
      await api.updateAiFeatureConfig(practiceId, featureType, { enabled: enabled ? 1 : 0 });
      setConfigs(prev => prev.map(c => c.feature_type === featureType ? { ...c, enabled: enabled ? 1 : 0 } : c));
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  if (!practiceId) {
    return <div className="card"><p style={{ color: '#666' }}>Please select a practice to view AI Insights.</p></div>;
  }
  if (loading) return <div className="loading">Loading AI Insights...</div>;
  if (error) return <div className="card" style={{ color: '#dc3545' }}>Error: {error}</div>;

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'churn', label: 'Churn Risk' },
    { key: 'revenue', label: 'Revenue Forecast' },
    { key: 'sentiment', label: 'Sentiment' },
    { key: 'ltv', label: 'Patient LTV' },
    { key: 'campaigns', label: 'Campaign AI' },
    { key: 'config', label: 'Configuration' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: '12px' }}>
        <h2 style={{ margin: 0 }}>AI Insights</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
          <span style={{ color: '#6b7280' }}>Period:</span>
          {[{ v: '7', l: '7 days' }, { v: '30', l: '30 days' }, { v: '90', l: '90 days' }, { v: 'all', l: 'All time' }, { v: 'custom', l: 'Custom' }].map(opt => (
            <button key={opt.v} onClick={() => setDateRange(opt.v)}
              style={{ padding: '4px 12px', borderRadius: '6px', border: '1px solid', cursor: 'pointer', fontSize: '12px', fontWeight: 600,
                borderColor: dateRange === opt.v ? '#2563eb' : '#e2e8f0',
                background: dateRange === opt.v ? '#eff6ff' : '#fff',
                color: dateRange === opt.v ? '#2563eb' : '#374151',
              }}>
              {opt.l}
            </button>
          ))}
          {dateRange === 'custom' && (
            <>
              <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px' }} />
              <span style={{ color: '#9ca3af' }}>to</span>
              <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                style={{ padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '12px' }} />
            </>
          )}
        </div>
      </div>

      {/* Tab navigation */}
      <div style={styles.tabBar}>
        {tabs.map(t => (
          <button key={t.key} onClick={() => setActiveTab(t.key)}
            style={{ ...styles.tab, ...(activeTab === t.key ? styles.tabActive : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && <OverviewTab dashboard={dashboard} onComputeNoShow={handleComputeNoShow} computing={computing} />}
      {activeTab === 'churn' && <ChurnTab data={churnData} />}
      {activeTab === 'revenue' && <RevenueTab data={forecastData} />}
      {activeTab === 'sentiment' && <SentimentTab data={sentimentData} />}
      {activeTab === 'ltv' && <LtvTab data={ltvData} />}
      {activeTab === 'campaigns' && <CampaignTab data={optimizations} />}
      {activeTab === 'config' && <ConfigTab configs={configs} onToggle={handleToggleFeature} isAdmin={hasPermission('ai:config')} />}
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────
function OverviewTab({ dashboard, onComputeNoShow, computing }) {
  if (!dashboard) return <p style={{ color: '#888' }}>No AI data computed yet. Click "Compute No-Show Risk" to start.</p>;

  const cards = [
    { label: 'High-Risk Appointments', value: dashboard.noShow?.highRiskCount || 0, color: '#dc3545', icon: 'calendar' },
    { label: 'At-Risk Patients (Churn)', value: dashboard.churn?.highRiskCount || 0, color: '#fd7e14', icon: 'users' },
    { label: 'Frustrated Patients', value: dashboard.sentiment?.frustratedCount || 0, color: '#e83e8c', icon: 'alert' },
    { label: '7-Day Revenue Forecast', value: dashboard.revenue?.next7DayTotal ? `$${Number(dashboard.revenue.next7DayTotal).toLocaleString()}` : '--', color: '#28a745', icon: 'trending' },
  ];

  return (
    <div>
      <div style={styles.cardGrid}>
        {cards.map((c, i) => (
          <div key={i} className="card" style={{ ...styles.summaryCard, borderLeft: `4px solid ${c.color}` }}>
            <div style={styles.cardLabel}>{c.label}</div>
            <div style={{ ...styles.cardValue, color: c.color }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
        <button className="btn btn-primary" onClick={onComputeNoShow} disabled={computing}>
          {computing ? 'Computing...' : 'Compute No-Show Risk'}
        </button>
      </div>

      {/* LTV Distribution */}
      {dashboard.ltv?.distribution && dashboard.ltv.distribution.length > 0 && (
        <div className="card" style={{ marginTop: 20 }}>
          <h3 style={{ marginTop: 0 }}>Patient Value Distribution</h3>
          <div style={styles.barChart}>
            {dashboard.ltv.distribution.map((d, i) => (
              <div key={i} style={styles.barRow}>
                <span style={{ ...styles.tierBadge, background: tierColor(d.tier) }}>{d.tier}</span>
                <div style={styles.barTrack}>
                  <div style={{ ...styles.barFill, width: `${Math.min(100, (d.count / Math.max(1, dashboard.ltv.totalPatients)) * 100)}%`, background: tierColor(d.tier) }} />
                </div>
                <span style={styles.barLabel}>{d.count} patients</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Churn Tab ────────────────────────────────────────────
function ChurnTab({ data }) {
  if (!data) return <div className="loading">Loading churn data...</div>;

  return (
    <div>
      {data.distribution.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>Risk Distribution</h3>
          <div style={{ display: 'flex', gap: 20 }}>
            {data.distribution.map((d, i) => (
              <div key={i} style={{ textAlign: 'center' }}>
                <div style={{ ...styles.riskBadge, background: riskColor(d.risk_label) }}>{d.count}</div>
                <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>{d.risk_label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>At-Risk Patients</h3>
        {data.patients.length === 0 ? <p style={{ color: '#888' }}>No at-risk patients found.</p> : (
          <table className="table">
            <thead>
              <tr>
                <th>Patient</th><th>Risk Score</th><th>Label</th><th>Days Since Visit</th><th>Recommended Action</th>
              </tr>
            </thead>
            <tbody>
              {data.patients.map((p, i) => (
                <tr key={i}>
                  <td>{p.first_name} {p.last_name}</td>
                  <td><strong>{Math.round((p.churn_risk_score || 0) * 100)}%</strong></td>
                  <td><span style={{ ...styles.pill, background: riskColor(p.risk_label) }}>{p.risk_label}</span></td>
                  <td>{p.days_since_last_visit ?? '--'}</td>
                  <td>{p.recommended_action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Revenue Tab ──────────────────────────────────────────
function RevenueTab({ data }) {
  if (!data) return <div className="loading">Loading forecast...</div>;
  if (data.length === 0) return <div className="card"><p style={{ color: '#888' }}>No forecast data available. Revenue scoring needs to run first.</p></div>;

  const maxRevenue = Math.max(...data.map(d => parseFloat(d.confidence_interval_high) || 0), 1);

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>30-Day Revenue Forecast</h3>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 200, minWidth: data.length * 20 }}>
          {data.map((d, i) => {
            const predicted = parseFloat(d.predicted_revenue) || 0;
            const height = (predicted / maxRevenue) * 180;
            const isWeekend = [0, 6].includes(new Date(d.forecast_date).getDay());
            return (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}
                title={`${d.forecast_date}: $${predicted.toFixed(0)} (${d.confidence_interval_low}-${d.confidence_interval_high})`}>
                <div style={{ width: '100%', maxWidth: 16, height, background: isWeekend ? '#dee2e6' : '#4f46e5', borderRadius: '2px 2px 0 0' }} />
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: '#888' }}>
          <span>{data[0]?.forecast_date}</span>
          <span>{data[data.length - 1]?.forecast_date}</span>
        </div>
      </div>

      <table style={{ ...styles.table, marginTop: 16 }}>
        <thead><tr><th>Date</th><th>Predicted</th><th>Confidence Range</th><th>Actual</th></tr></thead>
        <tbody>
          {data.slice(0, 14).map((d, i) => (
            <tr key={i}>
              <td>{d.forecast_date}</td>
              <td><strong>${parseFloat(d.predicted_revenue || 0).toFixed(0)}</strong></td>
              <td>${parseFloat(d.confidence_interval_low || 0).toFixed(0)} - ${parseFloat(d.confidence_interval_high || 0).toFixed(0)}</td>
              <td>{d.actual_revenue ? `$${parseFloat(d.actual_revenue).toFixed(0)}` : '--'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Sentiment Tab ────────────────────────────────────────
function SentimentTab({ data }) {
  if (!data) return <div className="loading">Loading sentiment data...</div>;

  return (
    <div>
      {data.summary && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>Sentiment Summary (7 Days)</h3>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            <div style={styles.statBox}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#e83e8c' }}>{data.summary.frustratedPatients || 0}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Frustrated Patients</div>
            </div>
            <div style={styles.statBox}>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#fd7e14' }}>{data.summary.urgentPatients || 0}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Urgent Cases</div>
            </div>
            {(data.summary.distribution || []).map((d, i) => (
              <div key={i} style={styles.statBox}>
                <div style={{ fontSize: 24, fontWeight: 700, color: sentimentColor(d.sentiment_label) }}>{d.count}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{d.sentiment_label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Frustrated Patients (48h)</h3>
        {data.frustrated.length === 0 ? <p style={{ color: '#888' }}>No frustrated patients detected.</p> : (
          <table className="table">
            <thead><tr><th>Patient</th><th>Sentiment</th><th>Urgency</th><th>Latest Message</th></tr></thead>
            <tbody>
              {data.frustrated.map((p, i) => (
                <tr key={i}>
                  <td>{p.first_name} {p.last_name}</td>
                  <td><span style={{ ...styles.pill, background: sentimentColor(p.sentiment_label) }}>{p.sentiment_label}</span></td>
                  <td><span style={{ ...styles.pill, background: urgencyColor(p.urgency_level) }}>{p.urgency_level}</span></td>
                  <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.latest_message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── LTV Tab ──────────────────────────────────────────────
function LtvTab({ data }) {
  if (!data) return <div className="loading">Loading LTV data...</div>;

  return (
    <div>
      {data.distribution.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>LTV Tier Distribution</h3>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            {data.distribution.map((d, i) => (
              <div key={i} style={{ ...styles.statBox, borderLeft: `4px solid ${tierColor(d.tier)}` }}>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{d.count}</div>
                <div style={{ fontSize: 12, color: '#666' }}>{d.tier} (avg ${d.avgLtv?.toLocaleString()})</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Top Patients by Lifetime Value</h3>
        {data.patients.length === 0 ? <p style={{ color: '#888' }}>No LTV data computed yet.</p> : (
          <table className="table">
            <thead><tr><th>Patient</th><th>LTV Score</th><th>Tier</th><th>Historical Revenue</th><th>Predicted (12mo)</th></tr></thead>
            <tbody>
              {data.patients.map((p, i) => (
                <tr key={i}>
                  <td>{p.first_name} {p.last_name}</td>
                  <td><strong>${parseFloat(p.ltv_score || 0).toLocaleString()}</strong></td>
                  <td><span style={{ ...styles.pill, background: tierColor(p.ltv_tier) }}>{p.ltv_tier}</span></td>
                  <td>${parseFloat(p.total_historical_revenue || 0).toLocaleString()}</td>
                  <td>${parseFloat(p.predicted_revenue_next_12mo || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Campaign Tab ─────────────────────────────────────────
function CampaignTab({ data }) {
  if (!data) return <div className="loading">Loading optimization data...</div>;
  if (data.length === 0) return <div className="card"><p style={{ color: '#888' }}>No optimization recommendations yet. Campaign analysis runs weekly.</p></div>;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>AI Campaign Recommendations</h3>
      {data.map((opt, i) => (
        <div key={i} style={{ ...styles.optCard, borderLeft: `3px solid ${opt.status === 'applied' ? '#28a745' : '#4f46e5'}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>{opt.optimization_type?.replace(/_/g, ' ')}</strong>
              <span style={{ ...styles.pill, marginLeft: 8, background: opt.status === 'applied' ? '#28a745' : '#6c757d' }}>{opt.status}</span>
            </div>
            <div style={{ color: '#28a745', fontWeight: 600 }}>+{opt.expected_improvement_pct}% expected</div>
          </div>
          {opt.recommended_config && (
            <div style={{ fontSize: 13, color: '#555', marginTop: 6 }}>
              {JSON.stringify(opt.recommended_config)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Config Tab ───────────────────────────────────────────
function ConfigTab({ configs, onToggle, isAdmin }) {
  const featureLabels = {
    no_show_risk: 'No-Show Prediction',
    optimal_send_time: 'Optimal Send Time',
    channel_preference: 'Channel Preference',
    message_personalization: 'Message Personalization',
    churn_risk: 'Churn Prediction',
    revenue_forecast: 'Revenue Forecasting',
    sentiment: 'Sentiment Analysis',
    scheduling_preference: 'Smart Scheduling',
    reputation_response: 'Reputation Response AI',
    campaign_optimization: 'Campaign Optimizer',
    call_outcome: 'Call Outcome Predictor',
    patient_ltv: 'Patient Lifetime Value',
  };

  const allFeatures = Object.keys(featureLabels);
  const configMap = {};
  configs.forEach(c => { configMap[c.feature_type] = c; });

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>AI Feature Configuration</h3>
      {!isAdmin && <p style={{ color: '#888', fontSize: 13 }}>Only admins can modify feature settings.</p>}
      <table className="table">
        <thead><tr><th>Feature</th><th>Status</th><th>Action</th></tr></thead>
        <tbody>
          {allFeatures.map(ft => {
            const cfg = configMap[ft];
            const enabled = cfg?.enabled === 1;
            return (
              <tr key={ft}>
                <td>{featureLabels[ft]}</td>
                <td><span style={{ ...styles.pill, background: enabled ? '#28a745' : '#6c757d' }}>{enabled ? 'Enabled' : 'Disabled'}</span></td>
                <td>
                  {isAdmin && (
                    <button className={`btn btn-sm ${enabled ? 'btn-outline-danger' : 'btn-outline-success'}`}
                      style={{ padding: '2px 10px', fontSize: 12 }}
                      onClick={() => onToggle(ft, !enabled)}>
                      {enabled ? 'Disable' : 'Enable'}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────
function riskColor(label) {
  if (label === 'high') return '#dc3545';
  if (label === 'medium') return '#fd7e14';
  return '#28a745';
}

function tierColor(tier) {
  if (tier === 'platinum') return '#6f42c1';
  if (tier === 'gold') return '#ffc107';
  if (tier === 'silver') return '#adb5bd';
  return '#cd7f32';
}

function sentimentColor(label) {
  if (label === 'positive') return '#28a745';
  if (label === 'frustrated') return '#dc3545';
  if (label === 'negative') return '#dc3545';
  if (label === 'urgent') return '#e83e8c';
  return '#6c757d';
}

function urgencyColor(level) {
  if (level === 'high') return '#dc3545';
  if (level === 'medium') return '#fd7e14';
  return '#28a745';
}

// ─── Styles ───────────────────────────────────────────────
const styles = {
  tabBar: { display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e9ecef', paddingBottom: 0, overflowX: 'auto' },
  tab: { padding: '8px 16px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 14, color: '#666', borderBottom: '2px solid transparent', marginBottom: -2, whiteSpace: 'nowrap' },
  tabActive: { color: '#4f46e5', borderBottomColor: '#4f46e5', fontWeight: 600 },
  cardGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 },
  summaryCard: { padding: '16px 20px' },
  cardLabel: { fontSize: 13, color: '#666', marginBottom: 4 },
  cardValue: { fontSize: 28, fontWeight: 700 },
  pill: { display: 'inline-block', padding: '2px 8px', borderRadius: 10, color: '#fff', fontSize: 12, fontWeight: 500 },
  riskBadge: { width: 48, height: 48, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 18 },
  statBox: { padding: '12px 16px', background: '#f8f9fa', borderRadius: 8, minWidth: 100 },
  barChart: { display: 'flex', flexDirection: 'column', gap: 8 },
  barRow: { display: 'flex', alignItems: 'center', gap: 8 },
  tierBadge: { padding: '2px 10px', borderRadius: 10, color: '#fff', fontSize: 12, fontWeight: 500, minWidth: 70, textAlign: 'center' },
  barTrack: { flex: 1, height: 16, background: '#f0f0f0', borderRadius: 8, overflow: 'hidden' },
  barFill: { height: '100%', borderRadius: 8, transition: 'width 0.3s ease' },
  barLabel: { fontSize: 12, color: '#666', minWidth: 80 },
  optCard: { padding: 12, background: '#f8f9fa', borderRadius: 6, marginBottom: 10 },
};

export default AiInsights;
